import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { AuthContext } from "../../../../contexts/auth";
import ClientSpreadsheetService from "../../../../services/clientSpreadsheet.service";
import {
  AvailableField,
  ClientSpreadsheetRecord,
  FieldMapping,
  FieldMappingPayload,
  MAPPING_VALUE_STAGE_LABEL,
  MappingValidationItem,
  normalizeFieldMapping,
  REPEATS_GROUP_FILTER_MESSAGE,
  repeatsGroupFilter,
  SheetInspection,
  SpreadsheetCategory,
  TemplateInspection,
} from "../../../../types/clientSpreadsheet.type";
import MappingDialog from "./MappingDialog";
import ReplicateDialog from "./ReplicateDialog";
import { isReplicableMapping } from "./ReplicateDialog/replicate";
import BudgetGroupTemplate, {
  pickTemplateDropdowns,
  pickTemplateSections,
  pickTemplateSheets,
} from "./BudgetGroupTemplate";

type Props = {
  clientId: string;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

function mappingLabel(m: FieldMapping): string {
  const label = m.label || m.ourField;
  return m.valueStage
    ? `${label} — ${MAPPING_VALUE_STAGE_LABEL[m.valueStage]}`
    : label;
}

function mappingDestination(m: FieldMapping): string {
  if (m.type === "cell") return m.cellRef ?? "-";
  if (!m.columnHeader) return "-";
  return m.columnLetter
    ? `${m.columnHeader} (${m.columnLetter.toUpperCase()})`
    : m.columnHeader;
}

function categoryLabel(
  category: string | undefined,
  categoryMap: Record<string, string>
): string {
  if (!category) return "-";
  return categoryMap[category] ?? category;
}

function normalizeMappings(mappings: FieldMappingPayload[]): FieldMapping[] {
  return mappings.map((m) => normalizeFieldMapping(m));
}

export default function SpreadsheetTab({ clientId }: Props) {
  const { actionOptions } = useContext(AuthContext);
  const canManage = !!actionOptions.updateParceiros;

  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [resetMappings, setResetMappings] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showInspection, setShowInspection] = useState(false);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [mappingsLoaded, setMappingsLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<{
    index: number | null;
    data: Partial<FieldMapping> | null;
  }>({ index: null, data: null });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [replicateIndex, setReplicateIndex] = useState<number | null>(null);
  const [invalidRows, setInvalidRows] = useState<MappingValidationItem[]>([]);

  const {
    data: record,
    isLoading,
    isError,
  } = useQuery<ClientSpreadsheetRecord | null>({
    queryKey: ["client-spreadsheet", clientId],
    queryFn: async () => {
      try {
        return await ClientSpreadsheetService.getRecord(clientId);
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data && !mappingsLoaded) {
        setMappings(normalizeMappings(data.fieldMappings ?? []));
        setMappingsLoaded(true);
      }
    },
  });

  /**
   * A lista de abas do template vem daqui — e a modal de mapeamento nao
   * funciona sem ela: "Aba da planilha" e obrigatoria, entao com a inspecao
   * ausente o "Confirmar" nunca habilita e um mapeamento salvo aparece com a
   * aba em branco na edicao. Por isso a inspecao carrega junto com o registro,
   * em vez de depender do clique em "Inspecionar planilha" (MMV-2599).
   */
  const inspectionQueryKey = ["client-spreadsheet-inspection", clientId];

  const {
    data: inspection = null,
    isFetching: loadingInspect,
    refetch: refetchInspection,
  } = useQuery<TemplateInspection | null>({
    queryKey: inspectionQueryKey,
    queryFn: () => ClientSpreadsheetService.inspect(clientId),
    enabled: !!record,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    // Silencioso: o carregamento e automatico, e so o clique no botao avisa.
    onError: () => undefined,
  });

  const { data: availableFields = [] } = useQuery<AvailableField[]>({
    queryKey: ["spreadsheet-fields"],
    queryFn: () => ClientSpreadsheetService.getFields(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: categories = [] } = useQuery<SpreadsheetCategory[]>({
    queryKey: ["spreadsheet-categories"],
    queryFn: () => ClientSpreadsheetService.getCategories(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const categoryLabelMap = categories.reduce<Record<string, string>>(
    (acc, c) => {
      acc[c.categoryKey] = c.categoryLabel;
      return acc;
    },
    {}
  );

  const fieldLabelMap = availableFields.reduce<Record<string, string>>(
    (acc, f) => { acc[f.path] = f.label; return acc; },
    {}
  );

  const saveMappingsMutation = useMutation(
    (fm: FieldMapping[]) => ClientSpreadsheetService.saveMappings(clientId, fm),
    {
      onSuccess: (updated) => {
        queryClient.setQueryData(["client-spreadsheet", clientId], updated);
        setMappings(normalizeMappings(updated.fieldMappings ?? []));
        setInvalidRows([]);
        // O cruzamento agrupamentos x mapeamentos vem da inspecao: sem reler,
        // o aviso "Sem mapeamento, a secao nasce e fica vazia" continua
        // listando as secoes que acabaram de ser mapeadas.
        refetchInspection();
        toast.success("Mapeamentos salvos com sucesso.");
      },
      onError: (err: any) => {
        const body = err?.response?.data;
        if (body?.invalidMappings) {
          setInvalidRows(body.invalidMappings);
          toast.error("Alguns mapeamentos sao invalidos. Verifique os itens destacados.");
        } else {
          toast.error("Erro ao salvar mapeamentos.");
        }
      },
    }
  );

  const deleteTemplateMutation = useMutation(
    () => ClientSpreadsheetService.deleteTemplate(clientId),
    {
      onSuccess: () => {
        queryClient.setQueryData(["client-spreadsheet", clientId], null);
        setMappings([]);
        setMappingsLoaded(false);
        queryClient.removeQueries(inspectionQueryKey);
        setShowInspection(false);
        setDeleteConfirmOpen(false);
        toast.success("Template removido com sucesso.");
      },
      onError: () => { toast.error("Erro ao remover template."); },
    }
  );

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) {
      toast.error("Apenas arquivos .xlsx sao aceitos.");
      return;
    }
    setUploadingFile(true);
    try {
      const result = await ClientSpreadsheetService.uploadTemplate(
        clientId,
        file,
        resetMappings
      );
      queryClient.setQueryData(
        ["client-spreadsheet", clientId],
        result.record
      );
      setMappings(normalizeMappings(result.record.fieldMappings ?? []));
      setMappingsLoaded(true);
      // O upload ja devolve a inspecao do arquivo novo; aproveita em vez de
      // refazer a chamada.
      queryClient.setQueryData(inspectionQueryKey, result.inspection);
      setShowInspection(true);
      if (result.invalidMappingsRemoved > 0) {
        toast.warn(
          `${result.invalidMappingsRemoved} mapeamento(s) invalido(s) foram removidos automaticamente.`
        );
      } else {
        toast.success("Template enviado com sucesso.");
      }
    } catch {
      toast.error("Erro ao enviar o template.");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  /** O botao continua existindo para reler o template e abrir o painel. */
  async function handleInspect() {
    const result = await refetchInspection();

    if (result.isError || !result.data) {
      toast.error("Erro ao inspecionar o template.");
      return;
    }

    setShowInspection(true);
  }

  function openAddDialog() {
    setDialogData({ index: null, data: null });
    setDialogOpen(true);
  }

  function openEditDialog(index: number) {
    setDialogData({
      index,
      data: normalizeFieldMapping(mappings[index] as FieldMappingPayload),
    });
    setDialogOpen(true);
  }

  function handleDialogConfirm(mapping: FieldMapping) {
    if (dialogData.index !== null) {
      const updated = [...mappings];
      updated[dialogData.index] = mapping;
      setMappings(updated);
    } else {
      setMappings((prev) => [...prev, mapping]);
    }
    setDialogOpen(false);
  }

  function handleReplicateConfirm(created: FieldMapping[]) {
    setMappings((prev) => [...prev, ...created]);
    setReplicateIndex(null);
    toast.success(
      `${created.length} mapeamento(s) criado(s). Clique em "Salvar mapeamentos" para gravar.`
    );
  }

  function removeMapping(index: number) {
    setMappings((prev) => prev.filter((_, i) => i !== index));
    setInvalidRows((prev) => prev.filter((r) => r.index !== index));
  }

  const sheets: SheetInspection[] = inspection?.sheets ?? [];
  const templateSections = useMemo(
    () => pickTemplateSections(inspection?.sheets),
    [inspection]
  );
  const templateDropdowns = useMemo(
    () => pickTemplateDropdowns(inspection?.sheets),
    [inspection]
  );
  const templateSheets = useMemo(
    () => pickTemplateSheets(inspection?.sheets),
    [inspection]
  );

  // Alem do que o backend recusa ao salvar, marca o que ele ACEITA mas a
  // geracao ignora — senao o mapeamento parece valido e o bloco sai vazio.
  function isRowInvalid(index: number): boolean {
    return (
      invalidRows.some((r) => r.index === index && !r.valid) ||
      repeatsGroupFilter(mappings[index] ?? {})
    );
  }

  function getRowError(index: number): string | undefined {
    return (
      invalidRows.find((r) => r.index === index)?.message ??
      (repeatsGroupFilter(mappings[index] ?? {})
        ? REPEATS_GROUP_FILTER_MESSAGE
        : undefined)
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Erro ao carregar configuracao do template.
      </Alert>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={3} mt={1}>
      <input
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <MappingDialog
        open={dialogOpen}
        initialData={dialogData.data}
        sheets={sheets}
        onConfirm={handleDialogConfirm}
        onClose={() => setDialogOpen(false)}
      />

      <ReplicateDialog
        open={replicateIndex !== null}
        mappings={mappings}
        sourceIndex={replicateIndex}
        sheets={sheets}
        templateEntries={record?.budgetGroupTemplate}
        fieldLabels={fieldLabelMap}
        onConfirm={handleReplicateConfirm}
        onClose={() => setReplicateIndex(null)}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover template</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover o template e todos os mapeamentos
            deste cliente? Esta acao nao pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => deleteTemplateMutation.mutate()}
            color="error"
            variant="contained"
            style={{ textTransform: "none" }}
            disabled={deleteTemplateMutation.isLoading}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography fontWeight="bold" fontSize={15} mb={1}>
          Template (.xlsx)
        </Typography>

        {record ? (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={record.originalFileName}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                Atualizado em {formatDate(record.updatedAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {record.fieldMappings?.length ?? 0} mapeamento(s)
              </Typography>
            </Box>
            {canManage && (
              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={resetMappings}
                      onChange={(e) => setResetMappings(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      Limpar mapeamentos ao substituir
                    </Typography>
                  }
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    uploadingFile ? (
                      <CircularProgress size={14} />
                    ) : (
                      <FileUploadIcon />
                    )
                  }
                  disabled={uploadingFile}
                  style={{ textTransform: "none" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Substituir template
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    loadingInspect ? (
                      <CircularProgress size={14} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  disabled={loadingInspect}
                  style={{ textTransform: "none" }}
                  onClick={handleInspect}
                >
                  Inspecionar planilha
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  style={{ textTransform: "none" }}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Remover template
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Typography color="text.secondary" mb={2}>
              Nenhum template cadastrado para este cliente.
            </Typography>
            {canManage && (
              <Box
                border="2px dashed #1361A4"
                borderRadius={2}
                p={3}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
                sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f0f7ff" } }}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingFile ? (
                  <CircularProgress />
                ) : (
                  <FileUploadIcon sx={{ fontSize: 40, color: "#1361A4" }} />
                )}
                <Typography color="#1361A4" fontWeight="bold">
                  Clique ou arraste um arquivo .xlsx
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Apenas arquivos Excel (.xlsx) sao aceitos
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {record && (
        <Collapse in={showInspection}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography fontWeight="bold" fontSize={15}>
                Inspecao do template
              </Typography>
              <Button
                size="small"
                style={{ textTransform: "none" }}
                onClick={() => setShowInspection(false)}
              >
                Fechar
              </Button>
            </Box>
            {sheets.map((sheet) => (
              <Box key={sheet.name} mb={2}>
                <Typography variant="subtitle2" mb={0.5}>
                  Aba: <strong>{sheet.name}</strong> — linha de cabecalho
                  detectada: {sheet.headerRow}
                </Typography>
                {sheet.headers.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {sheet.headers.map((h) => (
                      <Chip
                        key={h.letter}
                        label={`${h.letter}: ${h.value}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Nenhum cabecalho detectado.
                  </Typography>
                )}
              </Box>
            ))}
            {inspection?.mappingValidation &&
              inspection.mappingValidation.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" mb={0.5}>
                    Validacao dos mapeamentos:
                  </Typography>
                  {inspection.mappingValidation.map((v) => (
                    <Alert
                      key={v.index}
                      severity={v.valid ? "success" : "warning"}
                      sx={{ mb: 0.5, py: 0 }}
                      icon={v.valid ? undefined : <WarningAmberIcon fontSize="inherit" />}
                    >
                      <Typography variant="caption">
                        [{v.ourField}] {v.valid ? "OK" : v.message}
                      </Typography>
                    </Alert>
                  ))}
                </>
              )}
          </Paper>
        </Collapse>
      )}

      {record && (
        <BudgetGroupTemplate
          clientId={clientId}
          canManage={canManage}
          savedTemplate={record.budgetGroupTemplate}
          mappings={mappings}
          validation={inspection?.budgetGroupTemplateValidation}
          templateSections={templateSections}
          templateDropdowns={templateDropdowns}
          templateSheets={templateSheets}
        />
      )}

      {record && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontWeight="bold" fontSize={15}>
              Mapeamentos de campos ({mappings.length})
            </Typography>
            {canManage && (
              <Button
                size="small"
                variant="outlined"
                style={{ textTransform: "none", borderColor: "#1361A4", color: "#1361A4" }}
                onClick={openAddDialog}
              >
                + Adicionar mapeamento
              </Button>
            )}
          </Box>

          {mappings.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              Nenhum mapeamento configurado. Adicione campos para preencher a
              planilha do cliente.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rotulo</TableCell>
                    <TableCell>Aba</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell>Campo MM</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Agrupamento</TableCell>
                    {canManage && <TableCell align="right">Acoes</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((m, i) => {
                    const invalid = isRowInvalid(i);
                    const error = getRowError(i);
                    return (
                      <Tooltip
                        key={i}
                        title={invalid && error ? error : ""}
                        placement="top"
                      >
                        <TableRow
                          sx={{
                            bgcolor: invalid ? "#fff3e0" : "inherit",
                            "&:hover": { bgcolor: invalid ? "#ffe0b2" : "#f5f5f5" },
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {invalid && (
                                <WarningAmberIcon
                                  fontSize="small"
                                  color="warning"
                                />
                              )}
                              <Typography variant="body2">
                                {mappingLabel(m)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{m.sheetName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={m.type === "cell" ? "Celula" : "Coluna"}
                              size="small"
                              color={m.type === "cell" ? "default" : "info"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {mappingDestination(m)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {fieldLabelMap[m.ourField] || m.ourField}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {categoryLabel(m.category, categoryLabelMap)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {m.group?.trim() || "Todos"}
                            </Typography>
                          </TableCell>
                          {canManage && (
                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                              {isReplicableMapping(m) && (
                                <Tooltip title="Replicar o bloco para outras secoes">
                                  <IconButton
                                    size="small"
                                    aria-label="Replicar para outras secoes"
                                    onClick={() => setReplicateIndex(i)}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => openEditDialog(i)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeMapping(i)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      </Tooltip>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {canManage && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                disabled={saveMappingsMutation.isLoading}
                style={{
                  background: "#1361A4",
                  color: "#fff",
                  textTransform: "none",
                }}
                onClick={() => saveMappingsMutation.mutate(mappings)}
              >
                {saveMappingsMutation.isLoading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  "Salvar mapeamentos"
                )}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
